import clsx from 'clsx';
import {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {useThemeSelector} from '../themes/theme-selector-context';
import {useSelectedLocale} from '../../i18n/selected-locale';

interface Props {
  className?: string;
}

import {resolveSiteLogoSrc, SITE_LOGO_ALT, SITE_LOGO_FOOTER_CLASS} from '@common/branding/site-logo';
import {useSettings} from '../../core/settings/use-settings';

const IINTRA_LOGO = '/images/intra-logo.png';
const X_LOGO = '/images/x-logo.svg';

const linkItems = [
  {label: 'Terms of Use', to: '/pages/terms-of-service'},
  {label: 'Privacy Policy', to: '/pages/privacy-policy'},
  {label: 'Cookie Policy', to: '/pages/cookie-policy'},
  {label: 'Contact', to: '/contact'},
  {label: 'Updates & News', to: '/pages/updates-news'},
] as const;

const linkClassName =
  'transition-colors hover:text-[#535353] border border-transparent rounded-[17px] p-[10px] bg-white';

const FOOTER_LOCALE_REGION: Record<string, string> = {
  en: 'United States',
  'en-us': 'United States',
  'en-gb': 'United Kingdom',
};

function getFooterLocaleLabel(
  locale?: {name?: string; language?: string} | null
): string {
  if (!locale?.name) {
    return 'English (United States)';
  }

  if (locale.name.includes('(')) {
    return locale.name;
  }

  const region =
    FOOTER_LOCALE_REGION[locale.language?.toLowerCase() ?? ''] ??
    FOOTER_LOCALE_REGION.en;

  return region ? `${locale.name} (${region})` : locale.name;
}

export function Footer({className}: Props) {
  const {selectedTheme, selectTheme} = useThemeSelector();
  const {locale} = useSelectedLocale();
  const {branding} = useSettings();
  const isDarkMode = !!selectedTheme?.is_dark;
  const localeName = getFooterLocaleLabel(locale);
  const logoSrc = resolveSiteLogoSrc(branding, isDarkMode);

  return (
    <footer
      className={clsx(
        'mx-auto mt-[44px] w-full max-w-[1847px] overflow-hidden rounded-[36px] border-2 border-[#d8562a] bg-[#e9ecf0] px-[20px] py-[22px] dark:bg-[#1d2128] lg:mt-[120px] lg:min-h-[238px] lg:rounded-[60px] lg:pl-[56px] lg:pr-[18px] lg:pb-[25px] lg:pt-[60px]',
        className
      )}
    >
      <div className="grid gap-[20px] lg:grid-cols-[1fr_auto_1fr] lg:grid-rows-[auto_1fr_auto] lg:items-start  lg:gap-y-0">
        <div className="flex flex-col items-start gap-[22px] lg:row-span-3">
          <div className="flex flex-wrap items-center gap-[20px]">
            <div className="flex items-center gap-[8px]">
              <img
                src={IINTRA_LOGO}
                alt="iintra"
                className="h-[32px] w-[32px] object-contain lg:h-[41px] lg:w-[41px]"
              />
              <span className="text-[22px] font-medium leading-none text-black dark:text-[#f3f4f6] lg:text-[24px]">
                iintra
              </span>
            </div>
            <Link to="/" aria-label="Go to homepage" onClick={() => window.scrollTo({top: 0, left: 0, behavior: 'instant'})}>
              <img
                src={logoSrc}
                alt={SITE_LOGO_ALT}
                className={SITE_LOGO_FOOTER_CLASS}
              />
            </Link>
          </div>

          <div className="ml-[0px] mt-[21px] w-fit">
            <p className="font-['Inter',sans-serif] text-left text-[16px] font-medium leading-normal text-[#6a7480] dark:text-[#b3bcc8]">
              {'\u00A9'} iintra 2026{' '}
              <span className="px-[6px] text-[#d8562a]">|</span>
              built with{' '}
            <span className="inline-block text-[22px] leading-none text-[#d8562a] align-[-2px]">
                {'\u2665'}
            </span>
            </p>
            <p className="font-['Inter',sans-serif] text-left text-[16px] font-medium leading-normal text-[#6a7480] dark:text-[#b3bcc8]">
              All rights reserved.
            </p>
          </div>
        </div>

        <nav
          aria-label="Footer links"
          className="justify-self-center mt-[40px]  lg:col-start-2 lg:row-start-2 lg:self-start"
        >
          <ul className="hidden items-center gap-[10px] min-[1024px]:max-[1051px]:gap-[5px] text-[16px] font-medium text-[#6a7480] dark:text-[#b3bcc8] lg:flex">
            {linkItems.map((item, index) => (
              <Fragment key={item.to}>
                <li>
                  <Link to={item.to} className={`${linkClassName}  text-[#535353] dark:text-[#b3bcc8] border border-white dark:border-[#000000] rounded-[24px] bg-white dark:bg-[#000000] py-[9px] px-[12px] font-normal`}>
                    {item.label}
                  </Link>
                </li>
              </Fragment>
            ))}
          </ul>
          <ul className="flex flex-wrap items-center justify-center gap-x-[14px] gap-y-[24px] text-[15px] font-medium text-[#6a7480] dark:text-[#b3bcc8] lg:hidden">
            {linkItems.map(item => (
              <li key={item.to}>
                <Link to={item.to} className={linkClassName}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-col items-center gap-[18px] text-center text-[16px]  font-medium text-[#6a7480] dark:text-[#b3bcc8] lg:col-start-3 lg:row-span-3 lg:items-end lg:justify-self-end">
        <button
          type="button"
          onClick={() => selectTheme(isDarkMode ? 'light' : 'dark')}
          className="transition-colors text-[#535353] dark:text-[#b3bcc8] font-normal hover:text-[#535353] border border-white dark:border-[#000000] rounded-[64px] bg-white dark:bg-[#000000] px-[4.2rem] pt-[5px] pb-[7px]"
        >
          {isDarkMode ? 'Dark mode' : 'Light mode'}
        </button>
        <p className="border text-[#535353] dark:text-[#b3bcc8] font-normal border-white dark:border-[#000000] rounded-[64px] bg-white dark:bg-[#000000] px-[1.6rem] pt-[5px] pb-[7px]">{localeName}</p>
        <p className="text-[#5258C9] dark:text-[#b3bcc8] font-normal border border-white dark:border-[#000000] rounded-[64px] bg-white dark:bg-[#000000] px-[0.5rem] pt-[8px] pb-[7px]">Update 2.0 [FutureIsBright]</p>
      </div>

        <div className="flex flex-wrap items-center justify-center gap-x-[8px] gap-y-[6px] text-[20px] font-medium text-[#6a7480] dark:text-[#b3bcc8] lg:col-start-2 lg:row-start-3 lg:self-end">
          <img
            src={X_LOGO}
            alt="X"
            className="h-[28px] w-[28px] object-contain dark:invert"
          />
           {/* <p className="mt-[5px] mb-[12px] px-[11px] dark:text-[#b3bcc8] pb-[5px] pt-[1px] font-['Inter',sans-serif] text-[30px] font-light leading-none text-[#1a1a1a]  dark:text-[#f3f4f6] sm:text-[34px] hero:h-[50px] hero:w-[37px] hero:text-[43px]">
                  x
            </p> */}
          <a href="https://x.com/iintraco" target="_blank" rel="noopener noreferrer" className="font-['Inter',sans-serif] hover:underline">@iintraco</a>
          <span className="text-[#d8562a]">|</span>
          <a href="https://x.com/cloudtransferco" target="_blank" rel="noopener noreferrer" className="font-['Inter',sans-serif] hover:underline">@cloudtransferco</a>
        </div>
      </div>
    </footer>
  );
}
